/**
 * v4.15.0 — Daily Timeline Strip.
 *
 * 24-hour visual ruler showing every block in the schedule per day of
 * the week. Conflict regions are highlighted with a red overlay so the
 * operator sees the problem instead of reasoning about it.
 *
 * Strict scope: visual only. Does NOT mutate state. Does NOT make
 * routing decisions. The authoritative resolution still runs on the
 * server. This is a cognition aid.
 */
import { useTranslation } from 'react-i18next';
import { PLATFORM_COLORS } from '../../card_fleet/PlatformIcon';
import { DAYS_FULL, DAY_LABEL_MAP } from './scheduleAnalyzer';

const TICKS = [
    { hour: 0,  label: '12A' },
    { hour: 6,  label: '6A' },
    { hour: 12, label: '12P' },
    { hour: 18, label: '6P' },
    { hour: 24, label: '12A' },
];

const platformColor = (p) => PLATFORM_COLORS[p] || '#94a3b8';

export default function TimelineStrip({ analysis, blocks }) {
    const { t } = useTranslation();
    if (!analysis || !analysis.segments) return null;

    // Pre-bucket segments + conflicts by day for O(1) lookup.
    const segByDay = {};
    DAYS_FULL.forEach((_, idx) => { segByDay[idx] = []; });
    analysis.segments.forEach(s => { segByDay[s.dayIdx].push(s); });

    const conflictsByDay = {};
    DAYS_FULL.forEach((_, idx) => { conflictsByDay[idx] = []; });
    analysis.conflictRegions.forEach(c => { conflictsByDay[c.dayIdx].push(c); });

    return (
        <div
            className="rounded-md border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-900/60 p-2.5"
            data-testid="timing-timeline-strip"
        >
            <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">
                    Daily Routing Preview
                </div>
                <div className="text-[10px] text-slate-400">
                    {analysis.hasConflicts ? (
                        <span className="text-red-500 font-semibold">
                            {analysis.conflictRegions.length} conflict{analysis.conflictRegions.length === 1 ? '' : 's'}
                        </span>
                    ) : analysis.segments.length === 0 ? (
                        <span>{t('cardTiming.noBlocks')}</span>
                    ) : (
                        <span className="text-emerald-500 font-semibold">{t('cardTiming.cleanSchedule')}</span>
                    )}
                </div>
            </div>

            {/* Time tick header */}
            <div className="relative h-3 mb-1 ml-10 text-[9px] uppercase tracking-wider text-slate-400">
                {TICKS.map(tk => (
                    <span
                        key={tk.hour}
                        className="absolute"
                        style={{
                            left: `${(tk.hour / 24) * 100}%`,
                            transform: tk.hour === 24
                                ? 'translateX(-100%)'
                                : tk.hour === 0 ? 'translateX(0)' : 'translateX(-50%)',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {tk.label}
                    </span>
                ))}
            </div>

            {/* One row per day */}
            <div className="space-y-1">
                {DAYS_FULL.map((day, dayIdx) => {
                    const segs = segByDay[dayIdx] || [];
                    const confs = conflictsByDay[dayIdx] || [];
                    const hasContent = segs.length > 0;
                    return (
                        <div key={day} className="flex items-center gap-2">
                            <div className="w-8 text-[10px] uppercase font-semibold text-slate-500 dark:text-slate-400 text-right">
                                {DAY_LABEL_MAP[day]}
                            </div>
                            <div
                                className="relative flex-1 h-5 rounded-sm overflow-hidden"
                                style={{
                                    background: hasContent
                                        ? 'rgba(148, 163, 184, 0.10)'
                                        : 'repeating-linear-gradient(45deg, rgba(148,163,184,0.06), rgba(148,163,184,0.06) 4px, rgba(148,163,184,0.12) 4px, rgba(148,163,184,0.12) 8px)',
                                    border: '1px solid rgba(148, 163, 184, 0.18)',
                                }}
                                data-testid={`timeline-row-${day}`}
                            >
                                {/* Vertical hour tick lines */}
                                {[6, 12, 18].map(h => (
                                    <div
                                        key={h}
                                        className="absolute top-0 bottom-0 pointer-events-none"
                                        style={{
                                            left: `${(h / 24) * 100}%`,
                                            width: '1px',
                                            background: 'rgba(148, 163, 184, 0.18)',
                                        }}
                                    />
                                ))}

                                {/* Platform-colored block segments */}
                                {segs.map((s, k) => {
                                    const left = (s.fromMin / (24 * 60)) * 100;
                                    const width = ((s.toMin - s.fromMin) / (24 * 60)) * 100;
                                    if (width <= 0) return null;
                                    const color = platformColor(s.platform);
                                    return (
                                        <div
                                            key={`${dayIdx}-${s.blockIndex}-${k}`}
                                            className="absolute top-0 bottom-0 flex items-center justify-center text-[9px] font-semibold text-white truncate px-1"
                                            style={{
                                                left: `${left}%`,
                                                width: `${width}%`,
                                                background: `linear-gradient(180deg, ${color}, ${color}d9)`,
                                                boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.18)',
                                                textShadow: '0 1px 0 rgba(0,0,0,0.25)',
                                            }}
                                            title={`Block #${s.blockIndex} · ${s.platform} · ${Math.floor(s.fromMin / 60)}:${String(s.fromMin % 60).padStart(2, '0')}–${Math.floor(s.toMin / 60)}:${String(s.toMin % 60).padStart(2, '0')}`}
                                        >
                                            {width > 7 ? `#${s.blockIndex}` : null}
                                        </div>
                                    );
                                })}

                                {/* Conflict overlay — diagonal red hash */}
                                {confs.map((c, k) => {
                                    const left = (c.fromMin / (24 * 60)) * 100;
                                    const width = ((c.toMin - c.fromMin) / (24 * 60)) * 100;
                                    if (width <= 0) return null;
                                    return (
                                        <div
                                            key={`c-${dayIdx}-${k}`}
                                            className="absolute top-0 bottom-0 pointer-events-none"
                                            style={{
                                                left: `${left}%`,
                                                width: `${width}%`,
                                                background: 'repeating-linear-gradient(135deg, rgba(239,68,68,0.55), rgba(239,68,68,0.55) 3px, rgba(239,68,68,0.25) 3px, rgba(239,68,68,0.25) 6px)',
                                                border: '1px solid rgba(239, 68, 68, 0.9)',
                                                boxShadow: '0 0 0 1px rgba(239,68,68,0.45) inset',
                                                animation: 'pulse 1.6s ease-in-out infinite',
                                            }}
                                            data-testid={`timeline-conflict-${day}-${k}`}
                                            title={`Conflict: Block #${c.indexA} ⇄ Block #${c.indexB}`}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
